import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics/track';

interface Favorite {
  id: string;
  user_id: string;
  listing_id: string;
  listing_type: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  asset_table: string;
  created_at: string;
}

interface FavoriteWithDetails extends Favorite {
  helicopter_listings?: any;
  engine_listings?: any;
  parts_listings?: any;
  job_listings?: any;
  service_listings?: any;
}

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch user's favorites with listing details
  const fetchFavorites = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_favorites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setFavorites((data as Favorite[]) || []);
      setFavoriteIds(new Set(data?.map(f => f.listing_id) || []));
      
      // Cache in localStorage for performance
      if (data) {
        localStorage.setItem(`favorites_${user.id}`, JSON.stringify(data.map(f => f.listing_id)));
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast({
        title: "Error",
        description: "Failed to load favorites",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Add to favorites with type
  const addFavorite = async (listingId: string, type: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services' = 'helicopter') => {
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to save favorites",
        variant: "destructive"
      });
      return false;
    }

    try {
      const assetTable = 
        type === 'helicopter' ? 'helicopter_listings' : 
        type === 'engine' ? 'engine_listings' : 
        type === 'parts' ? 'parts_listings' :
        type === 'jobs' ? 'job_listings' :
        'service_listings';
      
      const { error } = await supabase
        .from('user_favorites')
        .insert({
          user_id: user.id,
          listing_id: listingId,
          listing_type: type,
          asset_table: assetTable
        });

      if (error) throw error;

      trackEvent({
        event_name: 'favorite_added',
        event_category: 'engagement',
        asset_type: type,
        asset_id: listingId,
      });


      // Update local state
      const newFavorite: Favorite = {
        id: crypto.randomUUID(),
        user_id: user.id,
        listing_id: listingId,
        listing_type: type,
        asset_table: assetTable,
        created_at: new Date().toISOString()
      };
      
      setFavorites(prev => [newFavorite, ...prev]);
      setFavoriteIds(prev => new Set([...prev, listingId]));

      const typeLabel = type === 'helicopter' ? 'Helicopter' : type === 'engine' ? 'Engine' : type === 'parts' ? 'Parts' : type === 'jobs' ? 'Jobs' : 'Services';
      toast({
        title: "Added to favorites",
        description: `${typeLabel} listing saved to your favorites`
      });
      
      return true;
    } catch (error: any) {
      console.error('Error adding favorite:', error);
      if (error.code === '23505') {
        toast({
          title: "Already in favorites",
          description: "This listing is already in your favorites",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to add to favorites",
          variant: "destructive"
        });
      }
      return false;
    }
  };

  // Remove from favorites
  const removeFavorite = async (listingId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('listing_id', listingId);

      if (error) throw error;

      const removedType = favorites.find(f => f.listing_id === listingId)?.listing_type;
      trackEvent({
        event_name: 'favorite_removed',
        event_category: 'engagement',
        asset_type: removedType ?? null,
        asset_id: listingId,
      });


      // Update local state
      setFavorites(prev => prev.filter(f => f.listing_id !== listingId));
      setFavoriteIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(listingId);
        return newSet;
      });

      toast({
        title: "Removed from favorites",
        description: "Listing removed from your favorites"
      });
      
      return true;
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast({
        title: "Error",
        description: "Failed to remove from favorites",
        variant: "destructive"
      });
      return false;
    }
  };

  // Toggle favorite status with type
  const toggleFavorite = async (listingId: string, type: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services' = 'helicopter') => {
    if (favoriteIds.has(listingId)) {
      return await removeFavorite(listingId);
    } else {
      return await addFavorite(listingId, type);
    }
  };

  // Check if listing is favorited
  const isFavorite = (listingId: string) => favoriteIds.has(listingId);

  // Memoized favorites by type to prevent infinite loops
  const [helicopterFavorites, setHelicopterFavorites] = useState<Favorite[]>([]);
  const [engineFavorites, setEngineFavorites] = useState<Favorite[]>([]);
  const [partsFavorites, setPartsFavorites] = useState<Favorite[]>([]);
  const [jobsFavorites, setJobsFavorites] = useState<Favorite[]>([]);
  const [servicesFavorites, setServicesFavorites] = useState<Favorite[]>([]);

  useEffect(() => {
    setHelicopterFavorites(favorites.filter(f => f.listing_type === 'helicopter'));
    setEngineFavorites(favorites.filter(f => f.listing_type === 'engine'));
    setPartsFavorites(favorites.filter(f => f.listing_type === 'parts'));
    setJobsFavorites(favorites.filter(f => f.listing_type === 'jobs'));
    setServicesFavorites(favorites.filter(f => f.listing_type === 'services'));
  }, [favorites]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    // Load from localStorage immediately for better UX
    const cached = localStorage.getItem(`favorites_${user.id}`);
    if (cached) {
      try {
        const cachedIds = JSON.parse(cached);
        setFavoriteIds(new Set(cachedIds));
      } catch (e) {
        console.error('Error parsing cached favorites:', e);
      }
    }

    fetchFavorites();

    const channel = supabase
      .channel('favorites-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_favorites',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          fetchFavorites();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return {
    favorites,
    helicopterFavorites,
    engineFavorites,
    partsFavorites,
    jobsFavorites,
    servicesFavorites,
    loading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    refetch: fetchFavorites
  };
};
